import axios from 'axios'
import { Moment } from 'moment'
import queryString from 'query-string'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import {
  NotificationListResponse,
  NotificationStatus,
} from 'src/models/notifications.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export interface GetNotificationsQueryParams {
  count?: number
  page?: number
  style?: string
  status?: NotificationStatus
}

export const GET_NOTIFICATIONS_INIT = 'app/notifications/GET_NOTIFICATIONS_INIT'
export const GET_NOTIFICATIONS_SUCCESS =
  'app/notifications/GET_NOTIFICATIONS_SUCCESS'
export const GET_NOTIFICATIONS_FAILURE =
  'app/notifications/GET_NOTIFICATIONS_FAILURE'

export interface GetNotificationsSuccessPayload {
  notifications: NotificationListResponse[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
    totalItems: number
  }
}
export type GetNotificationsErrorPayload = BackendErrorPayload<string>

export interface GetNotificationsInitAction {
  type: typeof GET_NOTIFICATIONS_INIT
}
export interface GetNotificationsSuccessAction {
  type: typeof GET_NOTIFICATIONS_SUCCESS
  payload: GetNotificationsSuccessPayload
}
export interface GetNotificationsFailureAction {
  type: typeof GET_NOTIFICATIONS_FAILURE
  payload: GetNotificationsErrorPayload
}

export type GetNotificationsActions =
  | GetNotificationsInitAction
  | GetNotificationsSuccessAction
  | GetNotificationsFailureAction

const createGetNotificationsInitAction = (): GetNotificationsInitAction => ({
  type: GET_NOTIFICATIONS_INIT,
})
const createGetNotificationsSuccessAction = (
  payload: GetNotificationsSuccessPayload,
): GetNotificationsSuccessAction => ({
  type: GET_NOTIFICATIONS_SUCCESS,
  payload,
})

const createGetNotificationsFailureAction = (
  payload: GetNotificationsErrorPayload,
): GetNotificationsFailureAction => ({
  type: GET_NOTIFICATIONS_FAILURE,
  payload,
})

export const getNotifications = (
  queryParameters?: GetNotificationsQueryParams,
): ThunkAction<Promise<void>, RootState, void, GetNotificationsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetNotificationsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetNotificationsInitAction())
    return axios
      .get<GetNotificationsSuccessPayload>(
        `/users/notifications?${queryParameters ? queryString.stringify(queryParameters) : ''}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetNotificationsSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetNotificationsFailureAction(err.response.data))
        },
      )
  }
}

export const GET_NOTIFICATIONS_COUNT_INIT =
  'app/notifications/GET_NOTIFICATIONS_COUNT_INIT'
export const GET_NOTIFICATIONS_COUNT_SUCCESS =
  'app/notifications/GET_NOTIFICATIONS_COUNT_SUCCESS'
export const GET_NOTIFICATIONS_COUNT_FAILURE =
  'app/notifications/GET_NOTIFICATIONS_COUNT_FAILURE'

export interface GetNotificationsCountSuccessPayload {
  count: number
}
export type GetNotificationsCountErrorPayload = BackendErrorPayload<string>

export interface GetNotificationsCountInitAction {
  type: typeof GET_NOTIFICATIONS_COUNT_INIT
}
export interface GetNotificationsCountSuccessAction {
  type: typeof GET_NOTIFICATIONS_COUNT_SUCCESS
  payload: GetNotificationsCountSuccessPayload
}
export interface GetNotificationsCountFailureAction {
  type: typeof GET_NOTIFICATIONS_COUNT_FAILURE
  payload: GetNotificationsCountErrorPayload
}

export type GetNotificationsCountActions =
  | GetNotificationsCountInitAction
  | GetNotificationsCountSuccessAction
  | GetNotificationsCountFailureAction

const createGetNotificationsCountInitAction =
  (): GetNotificationsCountInitAction => ({
    type: GET_NOTIFICATIONS_COUNT_INIT,
  })
const createGetNotificationsCountSuccessAction = (
  payload: GetNotificationsCountSuccessPayload,
): GetNotificationsCountSuccessAction => ({
  type: GET_NOTIFICATIONS_COUNT_SUCCESS,
  payload,
})

const createGetNotificationsCountFailureAction = (
  payload: GetNotificationsCountErrorPayload,
): GetNotificationsCountFailureAction => ({
  type: GET_NOTIFICATIONS_COUNT_FAILURE,
  payload,
})

export const getNotificationsCount = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetNotificationsCountActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetNotificationsCountActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetNotificationsCountInitAction())
    return axios
      .get<GetNotificationsCountSuccessPayload>(
        `/users/notifications/count`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetNotificationsCountSuccessAction(response.data))
          dispatch(getNotifications({ page: 0, count: 50 }))
        },
        async (err) => {
          dispatch(createGetNotificationsCountFailureAction(err.response.data))
        },
      )
  }
}

export const POST_READ_ALL_NOTIFICATIONS_INIT =
  'app/notifications/POST_READ_ALL_NOTIFICATIONS_INIT'
export const POST_READ_ALL_NOTIFICATIONS_SUCCESS =
  'app/notifications/POST_READ_ALL_NOTIFICATIONS_SUCCESS'
export const POST_READ_ALL_NOTIFICATIONS_FAILURE =
  'app/notifications/POST_READ_ALL_NOTIFICATIONS_FAILURE'

export interface PostReadAllNotificationsSuccessPayload {
  notificationIds: string[]
}
export interface PostReadAllNotificationsInitAction {
  type: typeof POST_READ_ALL_NOTIFICATIONS_INIT
}
export interface PostReadAllNotificationsSuccessAction {
  type: typeof POST_READ_ALL_NOTIFICATIONS_SUCCESS
  payload: PostReadAllNotificationsSuccessPayload
}
export interface PostReadAllNotificationsFailureAction {
  type: typeof POST_READ_ALL_NOTIFICATIONS_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostReadAllNotificationsActions =
  | PostReadAllNotificationsInitAction
  | PostReadAllNotificationsSuccessAction
  | PostReadAllNotificationsFailureAction

const createPostReadAllNotificationsInitAction =
  (): PostReadAllNotificationsInitAction => ({
    type: POST_READ_ALL_NOTIFICATIONS_INIT,
  })
const createPostReadAllNotificationsSuccessAction = (
  payload: PostReadAllNotificationsSuccessPayload,
): PostReadAllNotificationsSuccessAction => ({
  type: POST_READ_ALL_NOTIFICATIONS_SUCCESS,
  payload,
})
const createPostReadAllNotificationsFailureAction = (
  response: BackendErrorPayload<string>,
): PostReadAllNotificationsFailureAction => ({
  type: POST_READ_ALL_NOTIFICATIONS_FAILURE,
  payload: response,
})

export const postReadAllNotifications = (
  seenAt: Moment,
): ThunkAction<
  Promise<
    | PostReadAllNotificationsFailureAction
    | PostReadAllNotificationsSuccessAction
  >,
  RootState,
  void,
  PostReadAllNotificationsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostReadAllNotificationsActions>,
    getState: () => RootState,
  ): Promise<
    | PostReadAllNotificationsFailureAction
    | PostReadAllNotificationsSuccessAction
  > => {
    dispatch(createPostReadAllNotificationsInitAction())
    return axios
      .post<PostReadAllNotificationsSuccessPayload>(
        `/users/notifications/mark`,
        { seenAt: seenAt.add(1, 'ms').format('YYYY-MM-DDTHH:mm:ss.SSSZZ') }, // Add 1 ms to deal with rounding errors
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(
            createPostReadAllNotificationsSuccessAction(response.data),
          )
        },
        (err) => {
          return dispatch(
            createPostReadAllNotificationsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_NOTIFICATION_INIT = 'app/notifications/POST_NOTIFICATION_INIT'
export const POST_NOTIFICATION_SUCCESS =
  'app/notifications/POST_NOTIFICATION_SUCCESS'
export const POST_NOTIFICATION_FAILURE =
  'app/notifications/POST_NOTIFICATION_FAILURE'

export interface PostNotificationSuccessPayload {
  notificationId: string
}

export interface PostNotificationInitAction {
  type: typeof POST_NOTIFICATION_INIT
}
export interface PostNotificationSuccessAction {
  type: typeof POST_NOTIFICATION_SUCCESS
  payload: PostNotificationSuccessPayload
}
export interface PostNotificationFailureAction {
  type: typeof POST_NOTIFICATION_FAILURE
  payload: BackendErrorPayload<string>
}

export type PostNotificationActions =
  | PostNotificationInitAction
  | PostNotificationSuccessAction
  | PostNotificationFailureAction

const createPostNotificationInitAction = (): PostNotificationInitAction => ({
  type: POST_NOTIFICATION_INIT,
})
const createPostNotificationSuccessAction = (
  payload: PostNotificationSuccessPayload,
): PostNotificationSuccessAction => ({
  type: POST_NOTIFICATION_SUCCESS,
  payload,
})
const createPostNotificationFailureAction = (
  response: BackendErrorPayload<string>,
): PostNotificationFailureAction => ({
  type: POST_NOTIFICATION_FAILURE,
  payload: response,
})

export const postNotification = (
  notificationId: string,
): ThunkAction<
  Promise<PostNotificationFailureAction | PostNotificationSuccessAction>,
  RootState,
  void,
  PostNotificationActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostNotificationActions>,
    getState: () => RootState,
  ): Promise<PostNotificationFailureAction | PostNotificationSuccessAction> => {
    dispatch(createPostNotificationInitAction())
    return axios
      .post<PostNotificationSuccessPayload>(
        `/users/notifications/${notificationId}/mark`,
        {},
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createPostNotificationSuccessAction(response.data))
        },
        (err) => {
          return dispatch(
            createPostNotificationFailureAction(err.response.data),
          )
        },
      )
  }
}
